import { SignalsObjectType } from "../../SignalsModels";
import { getCardSizeSpec } from "./cardSizes";
import { LAYOUT_SCHEMA_VERSION } from "./grid";
import { GRID_COLUMNS } from "./layoutConfig";
import type {
    LayoutItem,
    LayoutObject,
    SignalsDashboardLayoutV1,
} from "./types";

/** Cards by their `order`, then id, with duplicates and blank ids dropped. */
export function sortLayoutObjects(
    objects: readonly LayoutObject[],
): LayoutObject[] {
    const seenIds = new Set<string>();

    return objects
        .filter((object) => {
            if (!object.id || seenIds.has(object.id)) return false;
            seenIds.add(object.id);
            return true;
        })
        .sort((left, right) => {
            const leftOrder = Number.isFinite(left.order) ? left.order : 0;
            const rightOrder = Number.isFinite(right.order) ? right.order : 0;
            return leftOrder - rightOrder || left.id.localeCompare(right.id);
        });
}

/**
 * The layout of a dashboard nobody has arranged, built from card order. Runs
 * of small cards pack across a row; every other card takes a full-width row.
 */
export function createDefaultItems(
    objects: readonly LayoutObject[],
): LayoutItem[] {
    const smallCardSpec = getCardSizeSpec({
        objectType: SignalsObjectType.SmallCard,
    });
    const items: LayoutItem[] = [];
    let currentY = 0;
    let nextSmallCardX = 0;

    const closeSmallCardRow = () => {
        if (nextSmallCardX === 0) return;
        currentY += smallCardSpec.defaultHeight;
        nextSmallCardX = 0;
    };

    sortLayoutObjects(objects).forEach((object) => {
        const spec = getCardSizeSpec(object);

        if (object.objectType === SignalsObjectType.SmallCard) {
            if (nextSmallCardX + spec.defaultWidth > GRID_COLUMNS) {
                closeSmallCardRow();
            }

            items.push({
                objectId: object.id,
                x: nextSmallCardX,
                y: currentY,
                width: spec.defaultWidth,
                height: spec.defaultHeight,
            });
            nextSmallCardX += spec.defaultWidth;

            if (nextSmallCardX === GRID_COLUMNS) closeSmallCardRow();
            return;
        }

        closeSmallCardRow();
        items.push({
            objectId: object.id,
            x: 0,
            y: currentY,
            width: spec.defaultWidth,
            height: spec.defaultHeight,
        });
        currentY += spec.defaultHeight;
    });

    return items;
}

export function createDefaultLayout(
    objects: readonly LayoutObject[],
): SignalsDashboardLayoutV1 {
    return {
        schemaVersion: LAYOUT_SCHEMA_VERSION,
        columns: GRID_COLUMNS,
        items: createDefaultItems(objects),
    };
}
